import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import axios from 'axios';

export function ResetPassword(): JSX.Element {
  const { token } = useParams({ from: '/reset-password/$token' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setPending(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError('This reset link is invalid or has expired.');
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 space-y-4">
        <h1 className="text-3xl font-normal text-ink">Password updated</h1>
        <p className="text-sm text-muted-foreground">Your password has been changed. You can sign in with it now.</p>
        <Link to="/login" className="btn-primary">
          Go to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="text-3xl font-normal mb-8 text-ink">Choose a new password</h1>
      <form className="space-y-3" onSubmit={submit}>
        <input
          className="input"
          type="password"
          placeholder="New password (≥ 8 chars)"
          aria-label="New password (≥ 8 chars)"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <input
          className="input"
          type="password"
          placeholder="Confirm new password"
          aria-label="Confirm new password"
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}{' '}
            {error.startsWith('This reset link') && (
              <Link to="/forgot-password" className="underline underline-offset-4">Request a new one</Link>
            )}
          </p>
        )}
        <button type="submit" disabled={pending} className="btn-primary w-full">
          {pending ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </div>
  );
}
